import { useState, useEffect, useCallback, useMemo } from 'react';
import { championshipApi } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Loader2, Medal, CheckCircle, Clock, XCircle, AlertTriangle, Search } from 'lucide-react';
import { toast } from 'sonner';

const DELIVERY_OPTIONS = [
  { value: 'pending', label: 'Premiação', icon: Clock, color: 'text-[#71717A]' },
  { value: 'delivered_to_athlete', label: 'Sim, entregue ao atleta', icon: CheckCircle, color: 'text-green-500' },
  { value: 'delivered_to_professor', label: 'Sim, entregue ao professor', icon: CheckCircle, color: 'text-green-500' },
  { value: 'delivered_to_guardian', label: 'Sim, entregue ao responsável', icon: CheckCircle, color: 'text-green-500' },
  { value: 'delivered_to_teammate', label: 'Sim, entregue a outro atleta da equipe', icon: CheckCircle, color: 'text-green-500' },
  { value: 'no_show', label: 'Não, não compareceu', icon: XCircle, color: 'text-red-500' },
  { value: 'out_of_stock', label: 'Não, acabou a medalha', icon: AlertTriangle, color: 'text-amber-500' },
  { value: 'arranged_later', label: 'Não, combinado entregar depois', icon: Clock, color: 'text-blue-400' },
];

const STATUS_FILTERS = [
  { value: 'all', label: 'Todos' },
  { value: 'pending', label: 'Pendentes' },
  { value: 'delivered', label: 'Entregues' },
  { value: 'no_show', label: 'Não Compareceu' },
  { value: 'out_of_stock', label: 'Acabou a Medalha' },
  { value: 'arranged_later', label: 'Combinado Depois' },
];

const MEDAL_EMOJI = { 1: '🥇', 2: '🥈', 3: '🥉', 4: '🏅' };
const MEDAL_COLORS = {
  gold: 'border-[#D4AF37]/40 bg-[#D4AF37]/5',
  silver: 'border-gray-400/40 bg-gray-400/5',
  bronze: 'border-amber-700/40 bg-amber-700/5',
  second_bronze: 'border-amber-700/30 bg-amber-700/5',
};

function matchesStatusFilter(medalist, filter) {
  if (filter === 'all') return true;
  if (filter === 'pending') return !medalist.delivery_status || medalist.delivery_status === 'pending';
  if (filter === 'delivered') return medalist.delivery_status?.startsWith('delivered_');
  return medalist.delivery_status === filter;
}

export default function MedalDeliveryWidget({ championshipId, bracketId, compact = false, onUpdate, refreshKey }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [brackets, setBrackets] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchData = useCallback(async () => {
    if (!championshipId) return;
    setLoading(true);
    try {
      const res = await championshipApi.getMedalDeliveries(championshipId, bracketId);
      setBrackets(res.data?.brackets || []);
    } catch (err) {
      console.error('Error fetching medal deliveries:', err);
    } finally {
      setLoading(false);
    }
  }, [championshipId, bracketId]);

  useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

  const handleStatusChange = async (bracketIdx, medalistIdx, newStatus) => {
    const bracket = brackets[bracketIdx];
    const medalist = bracket.medalists[medalistIdx];

    // Optimistic update
    setBrackets(prev => {
      const next = [...prev];
      const b = { ...next[bracketIdx] };
      const medalists = [...b.medalists];
      medalists[medalistIdx] = { ...medalist, delivery_status: newStatus };
      b.medalists = medalists;
      next[bracketIdx] = b;
      return next;
    });

    setSaving(true);
    try {
      await championshipApi.updateMedalDelivery(championshipId, [{
        athlete_id: medalist.athlete_id,
        bracket_id: bracket.bracket_id,
        medal_type: medalist.medal_type,
        delivery_status: newStatus,
      }]);
      toast.success('Status de entrega atualizado');
      onUpdate?.();
    } catch (err) {
      toast.error('Erro ao atualizar entrega');
      // Revert on error
      fetchData();
    } finally {
      setSaving(false);
    }
  };

  // Filter brackets and medalists based on search and status
  const filteredBrackets = useMemo(() => {
    const searchLower = search.toLowerCase().trim();
    return brackets.map(bracket => {
      const filteredMedalists = bracket.medalists.filter(m => {
        const matchesSearch = !searchLower || m.athlete_name?.toLowerCase().includes(searchLower);
        const matchesStatus = matchesStatusFilter(m, statusFilter);
        return matchesSearch && matchesStatus;
      });
      return { ...bracket, medalists: filteredMedalists };
    }).filter(bracket => bracket.medalists.length > 0);
  }, [brackets, search, statusFilter]);

  // Summary counts for status chips
  const statusCounts = useMemo(() => {
    const counts = { all: 0, pending: 0, delivered: 0, no_show: 0, out_of_stock: '', arranged_later: 0 };
    for (const bracket of brackets) {
      for (const m of bracket.medalists) {
        counts.all++;
        if (!m.delivery_status || m.delivery_status === 'pending') counts.pending++;
        else if (m.delivery_status?.startsWith('delivered_')) counts.delivered++;
        else if (m.delivery_status === 'no_show') counts.no_show++;
        else if (m.delivery_status === 'out_of_stock') counts.out_of_stock++;
        else if (m.delivery_status === 'arranged_later') counts.arranged_later++;
      }
    }
    return counts;
  }, [brackets]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4 text-[#71717A] text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Carregando entregas...
      </div>
    );
  }

  if (brackets.length === 0) {
    return (
      <div className="py-4 text-[#71717A] text-sm">
        Nenhum atleta premiado encontrado para esta chave.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="medal-delivery-widget">
      {/* === Filters (only in full mode, not compact/Mesa Central) === */}
      {!compact && (
        <div className="space-y-3">
          {/* Search by name */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar atleta por nome..."
              className="pl-9 bg-[#141414] border-[#27272A] text-white text-sm"
              data-testid="delivery-search-input"
            />
          </div>

          {/* Status filter chips */}
          <div className="flex flex-wrap gap-2">
            {STATUS_FILTERS.map(f => (
              <button
                key={f.value}
                onClick={() => setStatusFilter(f.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  statusFilter === f.value
                    ? 'bg-[#D4AF37] text-black'
                    : 'bg-[#1A1A1A] border border-[#27272A] text-[#A1A1AA] hover:border-[#D4AF37]/40'
                }`}
                data-testid={`status-filter-${f.value}`}
              >
                {f.label}
                {f.value !== 'all' && (
                  <span className="ml-1.5 opacity-70">
                    ({statusCounts[f.value] ?? 0})
                  </span>
                )}
                {f.value === 'all' && (
                  <span className="ml-1.5 opacity-70">({statusCounts.all})</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* === Bracket cards === */}
      {filteredBrackets.length === 0 && (search || statusFilter !== 'all') && (
        <div className="py-4 text-[#71717A] text-sm text-center">
          Nenhum resultado encontrado para os filtros aplicados.
        </div>
      )}

      {filteredBrackets.map((bracket, bIdx) => (
        <div key={bracket.bracket_id} className={`rounded-lg border ${compact ? 'p-3' : 'p-4'} border-[#27272A] bg-[#1A1A1A]/50`}>
          {!compact && (
            <div className="flex items-center gap-2 mb-3">
              <Medal className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-sm font-semibold text-white font-['Chivo']">
                {bracket.category_name}
              </span>
              <span className="text-xs text-[#71717A]">
                ({bracket.medalists.length} atleta{bracket.medalists.length !== 1 ? 's' : ''})
              </span>
            </div>
          )}

          <div className={`space-y-2 ${compact ? '' : 'space-y-3'}`}>
            {bracket.medalists.map((m, mIdx) => {
              const isDelivered = m.delivery_status?.startsWith('delivered_');
              const isNoShow = m.delivery_status === 'no_show';
              const isProblem = m.delivery_status === 'out_of_stock';
              return (
                <div
                  key={`${bracket.bracket_id}-${m.athlete_id}`}
                  className={`flex items-center gap-3 ${compact ? 'py-1' : 'py-2'} ${
                    isDelivered ? 'opacity-75' : ''
                  }`}
                >
                  {/* Medal indicator */}
                  <span className="text-lg w-7 text-center shrink-0" title={`Colocação: ${m.place}º lugar`}>
                    {MEDAL_EMOJI[m.place] || '🏅'}
                  </span>

                  {/* Athlete info */}
                  <div className="flex-1 min-w-0">
                    <span className={`text-sm font-medium ${isDelivered ? 'text-[#A1A1AA]' : 'text-white'} truncate block`}>
                      {m.athlete_name}
                    </span>
                    {m.team_name && (
                      <span className="text-xs text-[#71717A] truncate block">{m.team_name}</span>
                    )}
                  </div>

                  {/* Delivery status dropdown */}
                  <div className="shrink-0 w-56">
                    <Select
                      value={m.delivery_status || 'pending'}
                      onValueChange={(val) => handleStatusChange(bIdx, mIdx, val)}
                    >
                      <SelectTrigger
                        className={`h-8 text-xs bg-[#141414] border-[#27272A] ${
                          isDelivered ? 'border-green-800/40 text-green-400' :
                          isNoShow ? 'border-red-800/40 text-red-400' :
                          isProblem ? 'border-amber-800/40 text-amber-400' :
                          'text-[#A1A1AA]'
                        }`}
                        data-testid={`delivery-status-${m.athlete_id}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#27272A] max-h-[300px]">
                        {DELIVERY_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value} className="text-white hover:bg-[#27272A] text-xs">
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
